import { Fragment, useEffect, useState } from "react";
import "./paneles.css";

/**
 * IA Arena: qué modelo poner en cada escalón, y la prueba que lo sostiene.
 *
 * ── Para qué está ──────────────────────────────────────────────────────────
 *
 * Hoy el modelo de cada escalón se elige por lo que se lee por ahí y por lo que
 * cuesta. Esto pone delante lo único que decide de verdad: **los mismos
 * dieciocho encargos, contra cada modelo, con el mismo andamiaje**. Aprobados,
 * tiempo y coste, uno al lado del otro.
 *
 * ── Lo que NO hace, y es a propósito ───────────────────────────────────────
 *
 * No cambia el modelo de producción. Enseña quién gana; ponerlo es otro botón,
 * en el Panel de Control, y lo pulsa una persona. Una pantalla que compara y
 * además cambia acaba cambiando por error el día que alguien mira la tabla.
 *
 * Y **no inventa el coste**: si una pasada no dejó consumo apuntado, se dice.
 * Un cero junto a dos números medidos se lee como «gratis».
 */

interface Corredor {
  casa: string;
  modelo: string;
  etiqueta: string;
  nota: string;
}

interface Pasada {
  id: string;
  corredor: string;
  escalon: string;
  aprobados: number;
  total: number;
  segundos: number;
  /** Céntimos. `null` cuando esa pasada no dejó consumo apuntado. */
  centimos: number | null;
  cuando: string;
  suspendieron: number[];
  nota?: string;
  /** `false`: la carrera no midió al modelo, y no compite por «el mejor». */
  valida?: boolean;
}

const ESCALONES = [
  { valor: "chat", titulo: "Chat normal" },
  { valor: "ligero", titulo: "Razonamiento ligero" },
  { valor: "complejo", titulo: "Trabajo complejo" },
  { valor: "excepcional", titulo: "Excepcional" },
];

export function Arena() {
  const [corredores, setCorredores] = useState<Corredor[]>([]);
  const [pasadas, setPasadas] = useState<Pasada[]>([]);
  const [escalon, setEscalon] = useState("complejo");
  const [elegidos, setElegidos] = useState<string[]>([]);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<string | null>(null);
  const [dice, setDice] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    fetch("/api/arena", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`el servidor contestó ${r.status}`))))
      .then((d: { corredores: Corredor[]; pasadas: Pasada[] }) => {
        if (!vivo) return;
        setCorredores(d.corredores);
        setPasadas(d.pasadas);
      })
      .catch((e: Error) => vivo && setFallo(e.message))
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, []);

  /** Lanzar gasta dinero de verdad: se confirma en la pantalla, no de un toque. */
  const [confirmando, setConfirmando] = useState(false);

  const lanzar = async () => {
    setDice(null);
    setConfirmando(false);
    const r = await fetch("/api/arena/carrera", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ escalon, corredores: elegidos }),
    });
    const d = (await r.json()) as { dice?: string; error?: string };
    setDice(d.dice ?? d.error ?? "no ha contestado nada");
  };

  if (cargando)
    return (
      <div className="pnl">
        <p className="pnl-sub">Mirando qué modelos hay y qué carreras se han corrido…</p>
      </div>
    );
  if (fallo)
    return (
      <div className="pnl">
        <div className="pnl-nota es-mal" role="alert">
          <p>No se ha podido leer la arena: {fallo}.</p>
        </div>
      </div>
    );

  const delEscalon = pasadas.filter((p) => p.escalon === escalon);
  /* El mejor del escalón: más aprobados y, a igualdad, el más barato. */
  const mejor = delEscalon
    .filter((p) => p.valida !== false)
    .sort((a, b) => b.aprobados / b.total - a.aprobados / a.total || (a.centimos ?? 1e9) - (b.centimos ?? 1e9))[0];
  const validas = delEscalon.filter((p) => p.valida !== false).length;

  return (
    <div className="pnl">
      <header className="pnl-cabecera">
        <div>
          <h1>IA Arena</h1>
          <p className="pnl-sub">
            Los mismos dieciocho encargos contra cada modelo, con el mismo andamiaje. Esto <strong>no cambia</strong> el
            modelo de producción: eso se hace en Control.
          </p>
        </div>
        <div className="pnl-vistas" role="group" aria-label="Escalón">
          {ESCALONES.map((e) => (
            <button key={e.valor} aria-pressed={escalon === e.valor} onClick={() => setEscalon(e.valor)}>
              {e.titulo}
            </button>
          ))}
        </div>
      </header>

      <section className="pnl-seccion">
        <header>
          <h2>Quién corre</h2>
          {elegidos.length > 0 && <span className="pnl-cuenta">{elegidos.length}</span>}
        </header>
        <div className="ar-corredores">
          {corredores.map((c) => {
            const id = `${c.casa}:${c.modelo}`;
            const puesto = elegidos.includes(id);
            return (
              <button
                key={id}
                type="button"
                className="pnl-chip"
                aria-pressed={puesto}
                title={c.nota}
                onClick={() => setElegidos((x) => (puesto ? x.filter((y) => y !== id) : [...x, id]))}
              >
                {c.etiqueta}
              </button>
            );
          })}
        </div>
        <div className="ar-lanzar">
          <span className="pnl-sub">
            {elegidos.length === 0
              ? "Elige al menos un modelo."
              : `${elegidos.length} ${elegidos.length === 1 ? "modelo" : "modelos"} · 18 encargos × 3 repeticiones cada uno · gasta de verdad`}
          </span>
          {confirmando ? (
            <div className="hy-pregunta">
              <span className="q">¿Lanzar {elegidos.length * 54} encargos?</span>
              <button className="pnl-boton plano" onClick={() => setConfirmando(false)}>
                Cancelar
              </button>
              <button className="pnl-boton si" onClick={() => void lanzar()}>
                Lanzar
              </button>
            </div>
          ) : (
            <button className="pnl-boton si" onClick={() => setConfirmando(true)} disabled={elegidos.length === 0}>
              Lanzar la carrera
            </button>
          )}
        </div>
      </section>

      {dice && (
        <div className="pnl-nota" role="status">
          <p>{dice}</p>
          <button aria-label="Cerrar" onClick={() => setDice(null)}>
            ×
          </button>
        </div>
      )}

      <section className="pnl-seccion">
        <header>
          <h2>Resultados</h2>
          <span className="pnl-cuenta">{delEscalon.length}</span>
        </header>
        {delEscalon.length === 0 ? (
          <p className="pnl-vacio">
            Todavía no se ha corrido ninguna carrera en este escalón. Elige modelos y lánzala.
          </p>
        ) : (
          <div className="ar-tabla">
            <table>
              <thead>
                <tr>
                  <th>Modelo</th>
                  <th>Aprobados</th>
                  <th>Tiempo</th>
                  <th>Coste</th>
                  <th>Suspendieron</th>
                  <th>Cuándo</th>
                </tr>
              </thead>
              <tbody>
                {delEscalon.map((p) => (
                  <Fragment key={p.id}>
                    <tr
                      className={
                        [
                          p === mejor && validas > 1 ? "mejor" : "",
                          p.valida === false ? "no-vale" : "",
                          p.nota ? "con-nota" : "",
                        ]
                          .filter(Boolean)
                          .join(" ") || undefined
                      }
                    >
                      <td>
                        {p.corredor}
                        {p.valida === false && <span className="pnl-cuenta es-mal ar-gana">no cuenta</span>}
                        {p === mejor && validas > 1 && <span className="pnl-cuenta es-ok ar-gana">el mejor</span>}
                      </td>
                      <td>
                        <span className="ar-barra">
                          <b
                            style={{
                              width: `${Math.round((p.aprobados / p.total) * 100)}%`,
                            }}
                          />
                        </span>
                        <strong>{p.aprobados}</strong> de {p.total}
                      </td>
                      <td>
                        {Math.floor(p.segundos / 60)}m {p.segundos % 60}s
                      </td>
                      {/* Sin consumo apuntado se dice, no se pone un cero: un cero se lee «gratis». */}
                      <td>
                        {p.centimos === null ? (
                          <span className="ar-tenue">sin apuntar</span>
                        ) : (
                          `${(p.centimos / 100).toFixed(2)} €`
                        )}
                      </td>
                      <td className="ar-tenue">{p.suspendieron.length === 0 ? "—" : p.suspendieron.join(", ")}</td>
                      <td className="ar-tenue">{p.cuando.slice(0, 16).replace("T", " ")}</td>
                    </tr>
                    {p.nota && (
                      <tr className="ar-nota">
                        <td colSpan={6}>{p.nota}</td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
